import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import colors from '@kcftech/design-tokens/colors';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getLocallyStoredAccessToken } from 'contexts/UserContext';
import { useTheme } from 'styled-components';
import { ThemeType } from 'styles/colorMode';
import { Environment } from 'utils/EnvironmentHelper';
import { buildRawSVG } from 'utils/svg';

// Raw SVG strings require hardcoded colors (CSS variables don't work)
const questionMarkSvgLight = buildRawSVG(faQuestionCircle, colors.navy['1000']);
const questionMarkSvgDark = buildRawSVG(faQuestionCircle, colors.grey['50']);
const questionMarkSvgByTheme: Record<ThemeType, string> = {
    light: questionMarkSvgLight,
    dark: questionMarkSvgDark,
    night: questionMarkSvgDark,
    'dark-original': questionMarkSvgDark,
};

export type FileLibraryObjectParams = {
    mediaType: string | undefined;
} & (
    | {
          src: string | undefined;
          storageKey?: never;
      }
    | {
          storageKey: string | undefined;
          src?: never;
      }
);

export function getFileObjectPath(storageKey: string): string {
    return `${Environment.apiBaseUrl}/api/v3.0/Files/object/${storageKey}`;
}

function getPath(storageKey: string | undefined): string | undefined {
    return storageKey ? getFileObjectPath(storageKey) : undefined;
}

// Source of truth for fetching a file by URL. colorMode is required so the
// dev fallback can return the right theme-matched placeholder SVG.
export async function fetchFileByPath(
    path: string,
    colorMode: ThemeType,
    mediaType: string | undefined,
): Promise<{ blob: Blob; blobUrl: string }> {
    const token = getLocallyStoredAccessToken();
    const res = await fetch(path, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
        if (process.env.NODE_ENV === 'development' && path.includes('cIcon')) {
            // stop yelling in the console, we don't copy these to devenvs
            const questionMarkBlob = new Blob([questionMarkSvgByTheme[colorMode]], {
                type: 'image/svg+xml',
            });
            return {
                blob: questionMarkBlob,
                blobUrl: URL.createObjectURL(questionMarkBlob).toString(),
            };
        }
        throw new Error('Could not fetch picture');
    }
    const blob = await res.blob();
    // this allows window.open open the file preview correctly
    const typedBlob = new Blob([blob], { type: mediaType ?? blob.type });
    return { blob: typedBlob, blobUrl: URL.createObjectURL(typedBlob).toString() };
}

export function useFileLibraryObject({
    src,
    storageKey,
    mediaType,
}: FileLibraryObjectParams) {
    const theme = useTheme();
    const path = src ?? getPath(storageKey);
    return useQuery({
        queryKey: ['picture', path, theme.colorMode],
        queryFn: () => {
            if (!path) {
                return undefined;
            }
            return fetchFileByPath(path, theme.colorMode, mediaType);
        },
        enabled: !!path,

        retry: false,
        refetchOnMount: false,
        placeholderData: keepPreviousData,
        staleTime: Infinity,

        // 1 hour
        gcTime: 1000 * 60 * 60,
    });
}
